import { type PropsWithChildren, useLayoutEffect, useRef } from "react";

import styles from "components/dashboard/Dashboard.module.scss";

const DashboardViewport = ({ children }: PropsWithChildren) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) {
      return undefined;
    }

    const fit = () => {
      if (!viewport.clientWidth || !viewport.clientHeight) {
        return;
      }
      // Measure at full size so the board can grow again after content or viewport changes.
      viewport.style.setProperty("--dashboard-scale", "1");
      const bounds = content.getBoundingClientRect();
      let scale = Math.min(
        1,
        viewport.clientWidth / Math.max(content.scrollWidth, bounds.width),
        viewport.clientHeight /
          Math.ceil(Math.max(content.scrollHeight, bounds.height)),
      );
      // Wider unscaled columns can reduce wrapping, allowing larger text to fit.
      let upper = 1;
      for (let attempt = 0; attempt < 8 && scale < upper; attempt += 1) {
        const candidate = (scale + upper) / 2;
        viewport.style.setProperty("--dashboard-scale", String(candidate));
        if (
          content.getBoundingClientRect().height <=
            viewport.clientHeight + 0.5 &&
          content.scrollWidth * candidate <= viewport.clientWidth + 0.5
        ) {
          scale = candidate;
        } else {
          upper = candidate;
        }
      }
      viewport.style.setProperty("--dashboard-scale", String(scale));
    };

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(viewport);
    observer.observe(content);
    Array.from(content.children).forEach(child => observer.observe(child));
    fit();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [children]);

  return (
    <div className={styles.viewport} ref={viewportRef}>
      <div className={styles.main} ref={contentRef}>
        {children}
      </div>
    </div>
  );
};

export default DashboardViewport;
